import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
	Alert,
	alpha,
	Autocomplete,
	Box,
	Button,
	Card,
	CardContent,
	CircularProgress,
	Divider,
	Grid,
	MenuItem,
	Snackbar,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { es } from 'date-fns/locale';

import type { AxiosResponse } from 'axios';
import type {
	IProviderDTO,
	IProviderPaymentCalendarGridDTO,
	ProviderPaymentCalendarRowKeyDTO,
	IProviderPaymentCalendarRowMetaDTO,
	IProviderPaymentCalendarCellDetailRequestDTO,
} from '../../types';
import { autocomplete, getProviderPaymentCalendarGrid } from '../../services';
import ProviderPaymentCalendarDetailModal from '../ProviderPaymentCalendarDetailModal';
import { countriesWithAgencyConfig } from '../../../auth/constants';
import { useTheme } from '@mui/styles';

const BORDER_COLOR = '#e0e0e0';

type AgencyOption = { id: number; name: string };

type DatePreset =
	| 'THIS_WEEK'
	| 'LAST_WEEK'
	| 'NEXT_WEEK'
	| 'LAST_15'
	| 'NEXT_15'
	| 'THIS_MONTH'
	| 'LAST_MONTH'
	| 'NEXT_MONTH'
	| 'CUSTOM';

type FiltersState = {
	agency: AgencyOption | null;
	providers: IProviderDTO[];
	from: string;
	to: string;
	preset: DatePreset;
};

// --- Funciones Auxiliares de Fecha ---
const formatDateISO = (dt: Date | null): string => {
	if (!dt || isNaN(dt.getTime())) return '';
	const yyyy = dt.getFullYear();
	const mm = String(dt.getMonth() + 1).padStart(2, '0');
	const dd = String(dt.getDate()).padStart(2, '0');
	return `${yyyy}-${mm}-${dd}`;
};

const parseISOToDate = (iso: string): Date | null => {
	if (!iso) return null;
	const [y, m, d] = iso.split('-').map(Number);
	return new Date(y, m - 1, d);
};

const isoToday = (): string => formatDateISO(new Date());

const addDays = (iso: string, days: number): string => {
	const [y, m, d] = iso.split('-').map(Number);
	const dt = new Date(y, m - 1, d);
	dt.setDate(dt.getDate() + days);
	return formatDateISO(dt);
};

const addMonths = (iso: string, months: number): string => {
	const [y, m, d] = iso.split('-').map(Number);
	const dt = new Date(y, m - 1, d);
	dt.setMonth(dt.getMonth() + months);
	return formatDateISO(dt);
};

const startOfWeekISO = (iso: string): string => {
	const [y, m, d] = iso.split('-').map(Number);
	const dt = new Date(y, m - 1, d);
	const day = dt.getDay();
	const diffToMon = day === 0 ? 6 : day - 1;
	dt.setDate(dt.getDate() - diffToMon);
	return formatDateISO(dt);
};

const endOfWeekISO = (iso: string): string => addDays(startOfWeekISO(iso), 6);
const startOfMonthISO = (iso: string): string => {
	const [y, m] = iso.split('-').map(Number);
	const dt = new Date(y, m - 1, 1);
	return formatDateISO(dt);
};
const endOfMonthISO = (iso: string): string => {
	const [y, m] = iso.split('-').map(Number);
	const dt = new Date(y, m, 0);
	return formatDateISO(dt);
};
const formatWeekdayLong = (iso: string): string => {
	const [y, m, d] = iso.split('-').map(Number);
	const dt = new Date(y, m - 1, d);
	return dt.toLocaleDateString('es-UY', { weekday: 'long' });
};
const formatDayMonth = (iso: string): string => {
	const [y, m, d] = iso.split('-').map(Number);
	const dt = new Date(y, m - 1, d);
	return dt.toLocaleDateString('es-UY', { day: '2-digit', month: 'long' });
};
const money = (n: number): string => n.toFixed(2).replace('.', ',');
const rowKeyToString = (k: ProviderPaymentCalendarRowKeyDTO): string =>
	k.type === 'GROUP' ? `G:${String(k.id)}` : `P:${String(k.id)}`;

export const ProviderPaymentCalendar = (): JSX.Element => {
	const providerColWidth = 340;
	const dayColWidth = 160;
	const MAX_MONTHS_RANGE = 3;
	const theme = useTheme();

	const MAIN_PURPLE = theme.palette.primary.main;
	const [loadingGrid, setLoadingGrid] = useState(false);
	const [grid, setGrid] = useState<IProviderPaymentCalendarGridDTO | null>(null);

	const [providerOptions, setProviderOptions] = useState<IProviderDTO[]>([]);
	const [loadingProviders, setLoadingProviders] = useState(false);

	const [expandedGroupIds, setExpandedGroupIds] = useState<number[]>([]);

	const [detailModalOpen, setDetailModalOpen] = useState(false);
	const [detailRequest, setDetailRequest] = useState<IProviderPaymentCalendarCellDetailRequestDTO | null>(null);

	const [alert, setAlert] = useState<{ open: boolean; severity: 'success' | 'error'; message: string }>({
		open: false,
		severity: 'success',
		message: '',
	});

	const today = useMemo(() => isoToday(), []);
	const defaultFrom = useMemo(() => today, [today]);
	const defaultTo = useMemo(() => addDays(today, 30), [today]);

	const [filters, setFilters] = useState<FiltersState>({
		agency: null,
		providers: [],
		from: defaultFrom,
		to: defaultTo,
		preset: 'CUSTOM',
	});

	const handleFromChange = (newValue: Date | null) => {
		if (!newValue) return;
		const newFrom = formatDateISO(newValue);
		let newTo = filters.to;
		if (newFrom > newTo) newTo = newFrom;
		const maxTo = addMonths(newFrom, MAX_MONTHS_RANGE);
		if (newTo > maxTo) {
			newTo = maxTo;
			setAlert({
				open: true,
				severity: 'success',
				message: `El rango se ajustó al máximo de ${MAX_MONTHS_RANGE} meses.`,
			});
		}
		setFilters((prev) => ({ ...prev, from: newFrom, to: newTo, preset: 'CUSTOM' }));
	};

	const handleToChange = (newValue: Date | null) => {
		if (!newValue) return;
		const newTo = formatDateISO(newValue);
		let newFrom = filters.from;
		if (newTo < newFrom) newFrom = newTo;
		const minFrom = addMonths(newTo, -MAX_MONTHS_RANGE);
		if (newFrom < minFrom) {
			newFrom = minFrom;
			setAlert({
				open: true,
				severity: 'success',
				message: `El rango se ajustó al máximo de ${MAX_MONTHS_RANGE} meses.`,
			});
		}
		setFilters((prev) => ({ ...prev, from: newFrom, to: newTo, preset: 'CUSTOM' }));
	};

	const handleCellDoubleClick = useCallback(
		(row: IProviderPaymentCalendarRowMetaDTO, date: string, totalAmount: number) => {
			if (totalAmount <= 0) return;
			let targetProviderIds: string[] = [];
			let targetGroupId: number | undefined = undefined;
			if (row.key.type === 'PROVIDER') {
				targetProviderIds = [row.key.id];
			} else if (row.key.type === 'GROUP') {
				targetGroupId = Number(row.key.id);
				if (filters.providers && filters.providers.length > 0) {
					targetProviderIds = filters.providers.map((p) => p.id);
				}
			}
			const request: IProviderPaymentCalendarCellDetailRequestDTO = {
				paymentDate: date,
				agencyId: filters.agency?.id,
				providerIds: targetProviderIds,
				groupId: targetGroupId,
			};
			setDetailRequest(request);
			setDetailModalOpen(true);
		},
		[filters.agency, filters.providers],
	);

	const providerAutocomplete = async (value: string) => {
		setLoadingProviders(true);
		try {
			const resp: AxiosResponse<IProviderDTO[]> = await autocomplete(value);
			setProviderOptions(resp.data ?? []);
		} catch (e: any) {
			setProviderOptions([]);
		} finally {
			setLoadingProviders(false);
		}
	};

	const fetchGrid = async (override?: { nextFilters?: FiltersState; nextExpandedGroupIds?: number[] }) => {
		const f = override?.nextFilters ?? filters;
		const ex = override?.nextExpandedGroupIds ?? expandedGroupIds;
		if (!f.from || !f.to) return;

		setLoadingGrid(true);
		try {
			const body = {
				from: f.from,
				to: f.to,
				agencyId: f.agency?.id,
				providerIds: f.providers.map((p) => p.id),
				expandedGroupIds: ex,
			};
			const resp: AxiosResponse<IProviderPaymentCalendarGridDTO> = await getProviderPaymentCalendarGrid(body);
			setGrid(resp.data);
		} catch (e: any) {
			setGrid(null);
			setAlert({ open: true, severity: 'error', message: 'Ocurrió un error al cargar el calendario' });
		} finally {
			setLoadingGrid(false);
		}
	};

	const applyPreset = (preset: DatePreset) => {
		const t = today;
		let nextFrom = filters.from;
		let nextTo = filters.to;

		if (preset === 'THIS_WEEK') {
			nextFrom = startOfWeekISO(t);
			nextTo = endOfWeekISO(t);
		} else if (preset === 'LAST_WEEK') {
			const startOfThisWeek = startOfWeekISO(t);
			const startOfLastWeek = addDays(startOfThisWeek, -7);
			nextFrom = startOfLastWeek;
			nextTo = addDays(startOfLastWeek, 6);
		} else if (preset === 'NEXT_WEEK') {
			const startOfThisWeek = startOfWeekISO(t);
			const startOfNextWeek = addDays(startOfThisWeek, 7);
			nextFrom = startOfNextWeek;
			nextTo = addDays(startOfNextWeek, 6);
		} else if (preset === 'LAST_15') {
			nextFrom = addDays(t, -15);
			nextTo = t;
		} else if (preset === 'NEXT_15') {
			nextFrom = t;
			nextTo = addDays(t, 15);
		} else if (preset === 'THIS_MONTH') {
			nextFrom = startOfMonthISO(t);
			nextTo = endOfMonthISO(t);
		} else if (preset === 'LAST_MONTH') {
			const startOfThisMonth = startOfMonthISO(t);
			const endOfLastMonth = addDays(startOfThisMonth, -1);
			nextFrom = startOfMonthISO(endOfLastMonth);
			nextTo = endOfLastMonth;
		} else if (preset === 'NEXT_MONTH') {
			const endOfThisMonth = endOfMonthISO(t);
			const startOfNextMonth = addDays(endOfThisMonth, 1);
			nextFrom = startOfNextMonth;
			nextTo = endOfMonthISO(startOfNextMonth);
		}
		const nextFilters: FiltersState = { ...filters, preset, from: nextFrom, to: nextTo };
		setFilters(nextFilters);
		fetchGrid({ nextFilters });
	};

	const toggleGroup = (groupId: number) => {
		const isNowOpen = expandedGroupIds.includes(groupId);
		const nextExpanded = isNowOpen ? expandedGroupIds.filter((x) => x !== groupId) : [...expandedGroupIds, groupId];
		setExpandedGroupIds(nextExpanded);
		fetchGrid({ nextExpandedGroupIds: nextExpanded });
	};

	useEffect(() => {
		fetchGrid();
	}, []);

	const cellMap = useMemo(() => {
		const m = new Map<string, number>();
		(grid?.cellValues ?? []).forEach((c) => {
			const rowKey = rowKeyToString(c.rowKey);
			m.set(`${rowKey}|${c.paymentDate}`, c.total);
		});
		return m;
	}, [grid]);

	const visibleRows = useMemo(() => {
		if (!grid) return [];
		const looseProviders: IProviderPaymentCalendarRowMetaDTO[] = [];
		const groupsMap = new Map<
			number,
			{ header: IProviderPaymentCalendarRowMetaDTO; children: IProviderPaymentCalendarRowMetaDTO[] }
		>();
		for (const row of grid.rowDefinitions) {
			if (row.key.type === 'GROUP') {
				const gid = Number(row.key.id);
				const existingGroup = groupsMap.get(gid);
				if (!existingGroup) {
					groupsMap.set(gid, { header: row, children: [] });
				} else {
					existingGroup.header = row;
				}
			} else {
				const gid = row.groupId != null ? Number(row.groupId) : null;
				if (gid != null) {
					let existingGroup = groupsMap.get(gid);
					if (!existingGroup) {
						existingGroup = {
							header: {
								key: { type: 'GROUP', id: gid },
								label: row.groupName || `Grupo ${gid}`,
								sortOrder: row.sortOrder - 0.1,
							},
							children: [],
						};
						groupsMap.set(gid, existingGroup);
					}
					existingGroup.children.push(row);
				} else {
					looseProviders.push(row);
				}
			}
		}
		looseProviders.sort((a, b) => a.sortOrder - b.sortOrder);
		const sortedGroups = Array.from(groupsMap.values()).sort((a, b) => a.header.sortOrder - b.header.sortOrder);
		const out: IProviderPaymentCalendarRowMetaDTO[] = [];
		out.push(...looseProviders);
		for (const group of sortedGroups) {
			out.push(group.header);
			const groupId = Number(group.header.key.id);
			if (expandedGroupIds.includes(groupId)) {
				group.children.sort((a, b) => a.sortOrder - b.sortOrder);
				out.push(...group.children);
			}
		}
		return out;
	}, [grid, expandedGroupIds]);

	const rowHoverBg = '#faf8fc';
	const cellWithDataBg = alpha(MAIN_PURPLE, 0.04);
	const stickyProviderBg = '#ffffff';
	const stickyGroupBg = '#f6f4f9';

	return (
		<LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
			<Box display="flex" flexDirection="column" gap={2}>
				<Card variant="outlined" sx={{ borderRadius: 2 }}>
					<CardContent>
						<Grid container spacing={2} alignItems="flex-start">
							<Grid item xs={12} md={4}>
								<TextField
									select
									label="Agencia (opcional)"
									size="small"
									fullWidth
									value={filters.agency?.id ?? ''}
									onChange={(e) => {
										const selectedId = Number(e.target.value);
										const found = countriesWithAgencyConfig.find((c) => c.agencyId === selectedId);
										setFilters((prev) => ({
											...prev,
											agency: found ? { id: found.agencyId, name: found.countryName } : null,
										}));
									}}
									SelectProps={
										{
											// eslint-disable-next-line react/display-name
											renderValue: (selectedId: any) => {
												const found = countriesWithAgencyConfig.find((c) => c.agencyId === Number(selectedId));
												if (!found) return null;

												return (
													<Box display="flex" alignItems="center" gap={1}>
														<Box component="img" src={found.agencyIcon} sx={{ width: 24, borderRadius: '2px' }} />
														<Typography variant="body2">{found.countryName}</Typography>
													</Box>
												);
											},
										} as any
									}
								>
									<MenuItem value="">
										<em>Ninguna</em>
									</MenuItem>
									{countriesWithAgencyConfig.map((c) => (
										<MenuItem key={c.agencyId} value={c.agencyId}>
											<Box display="flex" alignItems="center" gap={1}>
												<Box component="img" src={c.agencyIcon} sx={{ width: 24, borderRadius: '2px' }} />
												<Typography variant="body2">{c.countryName}</Typography>
											</Box>
										</MenuItem>
									))}
								</TextField>
							</Grid>

							<Grid item xs={12} md={8}>
								<Autocomplete<IProviderDTO, true, false, false>
									multiple
									options={providerOptions}
									loading={loadingProviders}
									getOptionLabel={(o) => `${o?.fantasyName ?? ''}${o?.businessName ? ` — ${o.businessName}` : ''}`}
									isOptionEqualToValue={(a, b) => a?.id === b?.id}
									value={filters.providers}
									onChange={(_, v) => setFilters((s) => ({ ...s, providers: v }))}
									onInputChange={(_, value) => {
										if (value && value.trim().length >= 2) providerAutocomplete(value.trim());
									}}
									renderInput={(params) => <TextField {...params} label="Proveedores (opcional)" size="small" />}
								/>
							</Grid>

							<Grid item xs={12} md={3}>
								<DatePicker
									label="Desde"
									inputFormat="dd/MM/yyyy"
									value={parseISOToDate(filters.from)}
									onChange={handleFromChange}
									renderInput={(params) => <TextField {...params} size="small" fullWidth />}
								/>
							</Grid>

							<Grid item xs={12} md={3}>
								<DatePicker
									label="Hasta"
									inputFormat="dd/MM/yyyy"
									value={parseISOToDate(filters.to)}
									onChange={handleToChange}
									renderInput={(params) => (
										<TextField {...params} size="small" fullWidth helperText={`Máx. ${MAX_MONTHS_RANGE} meses`} />
									)}
								/>
							</Grid>

							<Grid item xs={12} md={6}>
								<Box display="flex" gap={1} flexWrap="wrap">
									{[
										{ p: 'LAST_WEEK', l: 'Semana pasada' },
										{ p: 'THIS_WEEK', l: 'Esta semana' },
										{ p: 'NEXT_WEEK', l: 'Próxima semana' },
										{ p: 'LAST_15', l: 'Últimos 15 días' },
										{ p: 'NEXT_15', l: 'Próximos 15 días' },
										{ p: 'LAST_MONTH', l: 'Mes pasado' },
										{ p: 'THIS_MONTH', l: 'Este mes' },
										{ p: 'NEXT_MONTH', l: 'Próximo mes' },
									].map((item) => (
										<Button
											key={item.p}
											size="small"
											variant={filters.preset === item.p ? 'contained' : 'outlined'}
											onClick={() => applyPreset(item.p as DatePreset)}
											sx={
												filters.preset === item.p
													? { backgroundColor: MAIN_PURPLE, '&:hover': { backgroundColor: alpha(MAIN_PURPLE, 0.9) } }
													: { color: MAIN_PURPLE, borderColor: MAIN_PURPLE }
											}
										>
											{item.l}
										</Button>
									))}
								</Box>
							</Grid>

							<Grid item xs={12}>
								<Box display="flex" justifyContent="flex-end">
									<Button
										size="small"
										variant="contained"
										onClick={() => fetchGrid()}
										disabled={loadingGrid}
										sx={{ backgroundColor: MAIN_PURPLE, '&:hover': { backgroundColor: alpha(MAIN_PURPLE, 0.9) } }}
									>
										Aplicar filtros
									</Button>
								</Box>
							</Grid>
						</Grid>
					</CardContent>
				</Card>

				<Card variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
					<Box
						sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: '#fafafa' }}
					>
						<Typography variant="h6" sx={{ color: MAIN_PURPLE, fontWeight: 700 }}>
							Calendario
						</Typography>
					</Box>
					<Divider />
					<Box sx={{ position: 'relative' }}>
						{loadingGrid && (
							<Box
								sx={{
									position: 'absolute',
									top: 0,
									left: 0,
									width: '100%',
									height: '100%',
									bgcolor: alpha('#fff', 0.7),
									zIndex: 20,
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									backdropFilter: 'blur(1px)',
								}}
							>
								<CircularProgress size={60} thickness={4} sx={{ color: MAIN_PURPLE }} />
							</Box>
						)}
						<Box
							sx={{
								overflow: 'auto',
								maxHeight: '70vh',
								'&::-webkit-scrollbar': { height: '10px', width: '10px' },
								'&::-webkit-scrollbar-track': { backgroundColor: '#f1f1f1', borderRadius: '5px' },
								'&::-webkit-scrollbar-thumb': {
									backgroundColor: MAIN_PURPLE,
									borderRadius: '5px',
									border: '2px solid #f1f1f1',
								},
								'&::-webkit-scrollbar-thumb:hover': { backgroundColor: alpha(MAIN_PURPLE, 0.8) },
							}}
						>
							<Box sx={{ minWidth: providerColWidth + (grid?.columnDates?.length ?? 0) * dayColWidth }}>
								<Box sx={{ display: 'flex', position: 'sticky', top: 0, zIndex: 10, bgcolor: '#fff' }}>
									<Box
										sx={{
											boxSizing: 'border-box',
											flexShrink: 0,
											width: providerColWidth,
											p: 2,
											position: 'sticky',
											left: 0,
											zIndex: 11,
											bgcolor: '#fff',
											borderRight: `1px solid ${BORDER_COLOR}`,
											borderBottom: `2px solid ${BORDER_COLOR}`,
										}}
									>
										<Typography variant="caption" sx={{ fontWeight: 800, color: '#666' }}>
											GRUPO / PROVEEDOR
										</Typography>
									</Box>
									{grid?.columnDates.map((d) => (
										<Box
											key={d}
											sx={{
												boxSizing: 'border-box',
												flexShrink: 0,
												width: dayColWidth,
												p: 1,
												textAlign: 'center',
												borderRight: `1px solid ${BORDER_COLOR}`,
												borderBottom: `2px solid ${BORDER_COLOR}`,
												bgcolor: '#fff',
											}}
										>
											<Typography
												variant="caption"
												sx={{ color: MAIN_PURPLE, fontWeight: 700, textTransform: 'uppercase', fontSize: '0.65rem' }}
											>
												{formatWeekdayLong(d)}
											</Typography>
											<Typography variant="body2" sx={{ fontWeight: 600 }}>
												{formatDayMonth(d)}
											</Typography>
										</Box>
									))}
								</Box>
								{visibleRows.map((row) => {
									const rkStr = rowKeyToString(row.key);
									const isGroup = row.key.type === 'GROUP';
									const groupId = isGroup ? Number(row.key.id) : 0;
									const isExpanded = isGroup && expandedGroupIds.includes(groupId);
									return (
										<Box key={rkStr} display="flex" sx={{ '&:hover > div': { bgcolor: rowHoverBg } }}>
											<Box
												onClick={() => isGroup && toggleGroup(groupId)}
												sx={{
													boxSizing: 'border-box',
													flexShrink: 0,
													width: providerColWidth,
													p: 1.5,
													position: 'sticky',
													left: 0,
													zIndex: 5,
													bgcolor: isGroup ? stickyGroupBg : stickyProviderBg,
													borderRight: `1px solid ${BORDER_COLOR}`,
													borderBottom: `1px solid ${BORDER_COLOR}`,
													cursor: isGroup ? 'pointer' : 'default',
													pl: !isGroup && (row as any).groupId ? 5 : 2,
													display: 'flex',
													flexDirection: 'column',
													justifyContent: 'center',
													transition: 'background-color 0.2s',
												}}
											>
												<Typography
													variant="body2"
													sx={{
														fontWeight: isGroup ? 800 : 500,
														color: isGroup ? MAIN_PURPLE : 'inherit',
														display: 'flex',
														alignItems: 'center',
														gap: 1,
													}}
												>
													{isGroup && (
														<Box component="span" sx={{ fontSize: '0.8rem' }}>
															{isExpanded ? '▼' : '▶'}
														</Box>
													)}
													{row.label}
												</Typography>
												{row.businessName && (
													<Typography variant="caption" sx={{ color: '#888', fontSize: '0.7rem' }}>
														{row.businessName}
													</Typography>
												)}
											</Box>
											{grid?.columnDates.map((d) => {
												let total = cellMap.get(`${rkStr}|${d}`) ?? 0;
												if (isGroup && total === 0 && isExpanded) {
													let childSum = 0;
													visibleRows.forEach((child) => {
														if (child.key.type === 'PROVIDER' && child.groupId === groupId) {
															childSum += cellMap.get(`P:${child.key.id}|${d}`) ?? 0;
														}
													});
													total = childSum;
												}
												const hasTotal = total > 0;
												return (
													// --- AGREGADO TOOLTIP (Issue 2) ---
													<Tooltip
														key={`${rkStr}|${d}`}
														title={hasTotal ? 'Doble click para ver detalle' : ''}
														arrow
														placement="top"
													>
														<Box
															onDoubleClick={() => handleCellDoubleClick(row, d, total)}
															sx={{
																boxSizing: 'border-box',
																flexShrink: 0,
																width: dayColWidth,
																p: 1.5,
																textAlign: 'center',
																borderRight: `1px solid ${BORDER_COLOR}`,
																borderBottom: `1px solid ${BORDER_COLOR}`,
																bgcolor: hasTotal ? cellWithDataBg : '#fff',
																display: 'flex',
																alignItems: 'center',
																justifyContent: 'center',
																transition: 'background-color 0.2s',
																cursor: hasTotal ? 'pointer' : 'default',
																userSelect: 'none',
															}}
														>
															<Typography
																variant="body2"
																sx={{ fontWeight: hasTotal ? 700 : 400, color: hasTotal ? MAIN_PURPLE : '#bbb' }}
															>
																{hasTotal ? money(total) : '-'}
															</Typography>
														</Box>
													</Tooltip>
												);
											})}
										</Box>
									);
								})}
							</Box>
						</Box>
					</Box>
				</Card>

				<ProviderPaymentCalendarDetailModal
					open={detailModalOpen}
					onClose={() => setDetailModalOpen(false)}
					requestParams={detailRequest}
				/>
				<Snackbar open={alert.open} autoHideDuration={2500} onClose={() => setAlert((s) => ({ ...s, open: false }))}>
					<Alert variant="filled" severity={alert.severity}>
						{alert.message}
					</Alert>
				</Snackbar>
			</Box>
		</LocalizationProvider>
	);
};

export default ProviderPaymentCalendar;
